import { Injectable, Type } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ICustomCtaValue, IGlobalCtaValue } from '@campaign-form/campaign-form.interface';
import { TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { numberValidator } from '@shared/validators/number-validator';
import { v4 as uuidv4 } from 'uuid';
import { ExpectedKpiComponent } from './expected-kpi.component';
import { IExpectedKpi, IExpectedKpiForm } from './expected-kpi.interface';

@Injectable({
  providedIn: 'root',
})
export class ExpectedKpiService {
  public static getConfig(): {
    component: Type<ExpectedKpiComponent>;
    createForm: (item?: IExpectedKpi, creatorId?: string, isViewMode?: boolean) => FormGroup<IExpectedKpiForm>;
  } {
    return {
      component: ExpectedKpiComponent,
      createForm: (item?: IExpectedKpi, creatorId?: string, isViewMode?: boolean): FormGroup<IExpectedKpiForm> => {
        // The row's persisted expectedValue IS the global value (custom-mode edits never reach it -
        // they live in customCtaValues), so initialize globalCtaValues here when the item doesn't
        // carry it. The reconcile (M1/M2) and hasGlobalKpiValue rely on globalCtaValues, and rows
        // rebuilt from stored values (reopen after save, template forms) can have a null
        // globalCtaValues that would only be filled lazily by the row component's
        // initializeGlobalValues - a reconcile running before that (e.g. with cached availableKpis)
        // would read "no global value", run M2 and drop per-creator deleted markers.
        // exclusiveTo rows hold a creator's private value, never a real global one - the fallback
        // below must not mirror it into globalCtaValues as if it were.
        const itemGlobalCtaValues: IGlobalCtaValue[] | null = item?.globalCtaValues?.length
          ? item.globalCtaValues
          : !item?.exclusiveTo && item?.expectedValue !== null && item?.expectedValue !== undefined
            ? [{ value: String(item.expectedValue), valueName: 'expected_value' }]
            : null;

        const form = new FormGroup<IExpectedKpiForm>({
          customCtaValues: new FormControl<ICustomCtaValue[]>(item?.customCtaValues ?? [], { nonNullable: true }),
          exclusiveTo: new FormControl<string | null>(item?.exclusiveTo || creatorId || null),
          executionId: new FormControl<string | null>(item?.executionId ?? null),
          expectedValue: new FormControl<number | null>(item?.expectedValue ?? null, [
            Validators.min(1),
            numberValidator(),
          ]),
          globalCtaValues: new FormControl<IGlobalCtaValue[] | null>(itemGlobalCtaValues),
          id: new FormControl<string | null>(item?.id ?? uuidv4()),
          isNew: new FormControl<boolean>(!item?.id),
          kpi: new FormControl<TExpectedKpi | null>(item?.kpi ?? null),
        });

        if (isViewMode) {
          form.disable();
        }

        return form;
      },
    };
  }
}
